import { getMessage } from '@tcc/i18n';
import { ApplicationTreatment, SingleProductStats } from '../types/ApplicationTreatment';
import { ApplicationsData, Application, ApplicationDetails, ApplicationProduct } from '../types/Application';
import { RawMeasurementQU, ValueUnit } from '../types/Common';
import { BASE_FIELDVIEW_URL } from '../constants/urls';
import { IN_SEASON } from '../constants/mapLayerIds';

const THUMBNAIL_DIM = 70;
const FV_SPATIAL_BASE_IMAGE_URL_V2 = `${BASE_FIELDVIEW_URL}/harvest/geo/v2/thumbnail`;
const LAYER = IN_SEASON.APPLICATIONS;

const emptyMeasurement = (): RawMeasurementQU => ({ q: 0, u: '' });

export const valueUnitToRawMeasurement = (apiUnit?: ValueUnit | null): RawMeasurementQU => {
  // Legacy treatments omit measurements; default instead of crashing the legacy UI.
  if (
    !apiUnit ||
    typeof apiUnit.v !== 'number' ||
    !Number.isFinite(apiUnit.v) ||
    typeof apiUnit.u !== 'string'
  ) {
    return emptyMeasurement();
  }

  return {
    q: apiUnit.v,
    u: apiUnit.u,
  };
};

function transformProductStats(productStats: SingleProductStats[]): ApplicationProduct[] {
  return productStats.map((stat) => {
    const rawRate = valueUnitToRawMeasurement(stat.averageProductRate);
    const rawApplied = valueUnitToRawMeasurement(stat.totalAmountApplied);

    return {
      name: stat.name || getMessage('legend.unknown'),
      rate: rawRate,
      applied: rawApplied,
      growerInputUnit: stat.appliedGrowerInputUnit,
    };
  });
}

function calculateTotalAmountApplied(
  appliedArea: ApplicationTreatment['appliedArea'],
  applicationRate: ApplicationTreatment['averageApplicationRate'],
): RawMeasurementQU {
  const rateUnit = applicationRate.u;
  const amountUnit = rateUnit.includes('/') ? rateUnit.split('/')[0] : rateUnit;
  const totalAmountValue = appliedArea.v * applicationRate.v;

  return {
    q: totalAmountValue,
    u: amountUnit,
  };
}

function generateThumbnailUri(
  treatment: ApplicationTreatment,
  userUuid?: string,
  fieldUUID?: string
): string {
  if (!userUuid || !fieldUUID) {
    return '';
  }

  const startTime = new Date(treatment.startDateTime).getTime();
  const endTime = new Date(treatment.endDateTime).getTime();
  const year = new Date(treatment.endDateTime).getFullYear();
  const crop = encodeURIComponent(encodeURIComponent(treatment.passName));
  
  const firstPartURL = `${FV_SPATIAL_BASE_IMAGE_URL_V2}/${userUuid}/${fieldUUID}/${year}/${crop}/${LAYER}/${THUMBNAIL_DIM}.png`;
  const lastPartURL = `?startTime=${startTime}&endTime=${endTime}`;
  
  return firstPartURL + lastPartURL;
}

export function treatmentsToApplicationsData(
  treatments: ApplicationTreatment[],
  options?: {
    userUuid?: string;
    fieldUUID?: string;
  }
): ApplicationsData {
  const { userUuid, fieldUUID} = options || {};

  const applications: Application[] = treatments.map(treatment => {
    const rawArea = valueUnitToRawMeasurement(treatment.appliedArea);
    const rawApplicationRate = valueUnitToRawMeasurement(treatment.averageApplicationRate);
    const rawTotalAmountApplied = calculateTotalAmountApplied(
      treatment.appliedArea,
      treatment.averageApplicationRate,
    );
    const products = transformProductStats(treatment.productStats || []);
    const rawMachineSpeed = valueUnitToRawMeasurement(treatment.machineSpeed);
    const displayName = treatment.passName || '';

    const details: ApplicationDetails = {
      area: rawArea,
      applicationRate: rawApplicationRate,
      totalAmountApplied: rawTotalAmountApplied,
      machineSpeed: rawMachineSpeed,
      products,
    };

    return {
      name: displayName,
      date: new Date(treatment.endDateTime),
      thumbnailUri: generateThumbnailUri(treatment, userUuid, fieldUUID),
      details,
    };
  });

  return { applications };
}
